/**
 * AcenteHotels — admin/operasyon-only management UI for the shared
 * reservation hotel catalog.
 *
 * The catalog is fed automatically every time a reservation is saved (see
 * `_upsert_hotel` in backend/routers/panel.py), but staff also need an
 * explicit way to:
 *   • Pre-populate common hotels before agencies start typing.
 *   • Rename obvious typos (delete + re-add for now; edit endpoint is a
 *     later refactor).
 *   • Remove test/dupe entries.
 *
 * Layout: single-column list with search, an inline "add hotel" form on top,
 * and a delete button per row (admin-only per backend).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BedDouble, Plus, Search, Trash2, Hotel, Loader2, Archive, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";

export default function AcenteHotels() {
  const { user } = useAcenteAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [q, setQ] = useState("");

  const isAdmin = user?.role === "admin";
  // Which row (if any) is pending confirmation. `null` when the dialog is closed.
  const [pendingDelete, setPendingDelete] = useState(null);

  // Archive of soft-deleted hotels (admin-only). Collapsed by default so it
  // doesn't crowd the main list; expands with a chevron toggle.
  const [archive, setArchive] = useState([]);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const load = () => {
    setLoading(true);
    panelApi
      .get("/hotels")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  const loadArchive = () => {
    if (!isAdmin) return;
    panelApi
      .get("/hotels/deleted")
      .then((r) => setArchive(Array.isArray(r.data) ? r.data : []))
      .catch(() => setArchive([]));
  };

  useEffect(() => {
    load();
    loadArchive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((h) => (h.name || "").toLowerCase().includes(needle));
  }, [items, q]);

  const addHotel = async (e) => {
    e?.preventDefault?.();
    const n = name.trim();
    if (!n) return toast.error("Otel adı boş olamaz");
    setBusy(true);
    try {
      await panelApi.post("/hotels", { name: n });
      toast.success(`"${n}" otel listesine eklendi`);
      setName("");
      load();
      loadArchive();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Otel eklenemedi");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!isAdmin || !pendingDelete) return;
    const h = pendingDelete;
    setPendingDelete(null);
    try {
      await panelApi.delete(`/hotels/${h.id}`);
      toast.success("Otel silindi — arşive taşındı, gerekirse geri yükleyebilirsiniz.");
      setItems((prev) => prev.filter((x) => x.id !== h.id));
      loadArchive();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silme başarısız");
    }
  };

  const restoreHotel = async (h) => {
    if (!isAdmin) return;
    setRestoringId(h.id);
    try {
      await panelApi.post(`/hotels/${h.id}/restore`);
      toast.success(`"${h.name}" geri yüklendi ve listeye eklendi`);
      setArchive((prev) => prev.filter((x) => x.id !== h.id));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Geri yükleme başarısız");
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="acente-hotels-page">
      <header>
        <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2 inline-flex items-center gap-1">
          <BedDouble size={12} /> Otel Yönetimi
        </div>
        <h1 className="font-serif text-3xl text-brand-navy">Otel Listesi</h1>
        <p className="text-sm text-slate-600 mt-1">
          Buraya eklediğiniz oteller <b>rezervasyon oluşturma formunda acentelere görünür</b> ve
          seçilebilir listede yer alır. Acenteler istedikleri bir oteli listede bulamazsa
          "Başka Otel Yaz" seçeneği ile kendileri de manuel ekleyebilir. {isAdmin ? "" : "Silme yalnızca yöneticiye açıktır."}
        </p>
      </header>

      {/* Add new hotel */}
      <form
        onSubmit={addHotel}
        className="bg-white rounded-2xl border border-brand-navy/10 p-5 grid gap-3 sm:grid-cols-[1fr_auto] items-end"
        data-testid="hotel-add-form"
      >
        <div>
          <Label>Yeni Otel Adı</Label>
          <div className="relative">
            <Hotel size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn. Swissôtel Al Maqam"
              className="pl-9"
              data-testid="hotel-add-input"
              autoFocus
            />
          </div>
        </div>
        <Button
          type="submit"
          disabled={busy || !name.trim()}
          className="bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-60"
          data-testid="hotel-add-submit"
        >
          {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Plus size={14} className="mr-2" />} Ekle
        </Button>
      </form>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 p-3 flex items-center gap-2">
        <Search size={14} className="text-slate-400 ml-1" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Otel adına göre ara..."
          className="flex-1 bg-transparent outline-none text-sm"
          data-testid="hotel-search-input"
        />
        <span className="text-[11px] text-slate-500 mr-2" data-testid="hotel-count">
          {filtered.length} otel
        </span>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-slate-500 text-sm" data-testid="hotel-loading">
            <Loader2 size={16} className="mx-auto mb-2 animate-spin text-brand-orange" /> Yükleniyor…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="hotel-empty">
            <BedDouble size={28} className="mx-auto mb-3 text-slate-300" />
            {q ? "Aramanızla eşleşen otel yok." : "Henüz otel yok — üstteki formdan ekleyin."}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((h) => (
              <li
                key={h.id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-canvas-stone/40 transition"
                data-testid={`hotel-row-${h.id}`}
              >
                <div className="w-9 h-9 rounded-xl bg-brand-orange/10 text-brand-orange-dark flex items-center justify-center flex-shrink-0">
                  <Hotel size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-brand-navy font-medium truncate">{h.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {(h.usage_count || 0) === 0 ? "Henüz kullanılmadı" : `${h.usage_count}× rezervasyonda kullanıldı`}
                  </div>
                </div>
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPendingDelete(h)}
                    className="text-rose-600 border-rose-200 hover:bg-rose-50"
                    data-testid={`hotel-delete-${h.id}`}
                  >
                    <Trash2 size={13} className="mr-1" /> Sil
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="hotel-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Oteli silmek istediğinize emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              <b className="text-brand-navy">"{pendingDelete?.name}"</b> açılır listeden kaldırılacak.
              Geçmiş rezervasyonlarda otel adı olduğu gibi kalır; yalnızca yeni rezervasyon
              formundaki açılır menüden çıkar. <br/>
              <span className="text-emerald-700">Fikriniz değişirse aşağıdaki "Silinen Oteller" arşivinden tek tıkla geri yükleyebilirsiniz.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="hotel-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="hotel-delete-confirm"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Silinen Oteller Arşivi — admin only */}
      {isAdmin && (
        <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden" data-testid="hotel-archive-section">
          <button
            type="button"
            onClick={() => setArchiveOpen((v) => !v)}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-canvas-stone/40 transition text-left"
            data-testid="hotel-archive-toggle"
          >
            <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
              <Archive size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-brand-navy font-semibold">Silinen Oteller</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {archive.length === 0
                  ? "Arşivde silinmiş otel yok."
                  : `${archive.length} silinmiş otel — tek tıkla geri yükleyebilirsiniz.`}
              </div>
            </div>
            {archiveOpen ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
          </button>
          {archiveOpen && archive.length > 0 && (
            <ul className="divide-y divide-slate-100 border-t border-slate-100" data-testid="hotel-archive-list">
              {archive.map((h) => {
                const when = h.deleted_at ? new Date(h.deleted_at).toLocaleString("tr-TR", {
                  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
                }) : "";
                return (
                  <li
                    key={h.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-emerald-50/40 transition"
                    data-testid={`hotel-archive-row-${h.id}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0">
                      <Hotel size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-700 font-medium truncate line-through decoration-slate-300">{h.name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {when && <>Silindi: <span className="font-mono">{when}</span></>}
                        {h.deleted_by_name && <> · {h.deleted_by_name}</>}
                        {(h.usage_count || 0) > 0 && <> · {h.usage_count}× rezervasyonda kullanılmış</>}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => restoreHotel(h)}
                      disabled={restoringId === h.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                      data-testid={`hotel-restore-${h.id}`}
                    >
                      {restoringId === h.id
                        ? <Loader2 size={13} className="mr-1 animate-spin" />
                        : <RotateCcw size={13} className="mr-1" />}
                      Geri Yükle
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
